import { Directive, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { SelectComponent } from '@shared/select/select.component';
import { ProjectSection } from '../models/access.models';
import { AccessService } from '../services/access.service';

/**
 * Ролевая модель (F2): авто-дизейбл `app-select` для read-only ролей.
 *
 * Многие селекты привязаны через `(onChange)`/`[value]`, а не `formControlName`, поэтому
 * `form.disable()` их не отключает. Эта директива матчится на каждый `app-select` и, если
 * нет права `<section>:edit` (секция — из `data.section` маршрута), ставит селекту `disabled`
 * (список не открывается, выбор/ввод заблокированы — см. guard'ы в SelectComponent).
 *
 * Работает только в модулях, импортирующих AccessUiModule.
 */
@Directive({ selector: 'app-select' })
export class PermSelectDirective implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _weDisabled = false;

  constructor(
    @Optional() @Self() private _select: SelectComponent,
    private _route: ActivatedRoute,
    private _access: AccessService,
  ) {}

  ngOnInit(): void {
    const data = this._route.snapshot.data ?? {};
    const section = data['section'] as ProjectSection | undefined;
    if (!section || data['skipPermDisable'] || !this._select) return;

    this._access
      .can$(section, 'edit')
      .pipe(takeUntil(this._destroy$))
      .subscribe((canEdit) => {
        if (!canEdit) {
          this._select.setDisabledState(true);
          this._weDisabled = true;
        } else if (this._weDisabled) {
          this._select.setDisabledState(false);
          this._weDisabled = false;
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
